// T19 of docs/plans/2026-08-13-verification-integration-closeout.md: the
// module made visible. Everything before this ticket built machinery that
// nothing in the running app called.
//
// This is what the two public surfaces mount — PublicVideo.tsx and
// EmbedVideo.tsx — and it is deliberately one component rather than the same
// forty lines written twice: the badge, the addendum merge and the props the
// player needs are one decision each, not two.
//
// THREE PROPERTIES ARE LOAD-BEARING HERE. Each is structural — a reader can
// check them by looking at the code below rather than by trusting a comment.
//
//  1. A VIDEO WITH NO CHAIN POINTER OF ITS OWN OR ITS CONTAINER'S TAKES THE
//     OLD PATH, EXACTLY. No provider is mounted, no hook runs, no request is
//     made, and `subtitleLanguages` is the same `string[]` the page passed
//     before this component existed — no glyph, no panel, nothing. Most videos
//     are in this state and it is not a failure; there is simply nothing on
//     chain to check against. A version with no authority of its own but whose
//     container WAS anchored still takes the verifying path below, because
//     the container's version list is exactly what can tell a viewer this
//     is not the version the chain names (see verificationPointer.ts).
//
//  2. NO WALLET IS INVOLVED, and none can be. This mounts a bare
//     ConnectionProvider, NOT SolanaProvider: everything the verification
//     module reads goes through `useProgramReader`/`useForumVideo`/
//     `useForumContainer`, which need a connection and nothing else. Mounting
//     the wallet stack on a public page would add an autoConnecting Phantom
//     adapter and a modal to a surface whose entire point is that a stranger
//     with no account and no wallet can check the video themselves.
//
//  3. THE GLYPH AND THE MODAL GO THROUGH THE PLAYER'S OWN SLOTS, not over the
//     top of it from here. Fullscreen targets the player's wrapper element,
//     so anything floated by this component would disappear exactly when a
//     viewer is most immersed — and the modal, which is where a viewer goes
//     to check what they are watching, is the last thing that should vanish
//     at that moment. VideoPlayer renders what it is handed and knows
//     nothing about what it means.
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ConnectionProvider } from '@solana/wallet-adapter-react'
import { PublicKey } from '@solana/web3.js'
import { rpcEndpoint, wsEndpoint } from '../solana/kit/index.js'
import { publisherPda, videoIdBytes, videoPda } from '../solana/forum/index.js'
import { solanaCluster, clusterRpcUrl, clusterWsUrl } from '../solana/endpoints.js'
import VideoPlayer, { type PlayerVerificationContext, type VideoPlayerProps } from '../player/VideoPlayer.js'
import { mergeSubtitleTracks } from '../player/useSubtitles.js'
import type { ContainerVersion, Video } from '../types.js'
import VerificationGlyph from './VerificationGlyph.js'
import VerificationModal from './VerificationModal.js'
import { useVideoVerification } from './useVideoVerification.js'
import { haltFromMismatch, mismatchFromHalt } from './failureSurface.js'
import { verificationPointer } from './verificationPointer.js'
import { activeFixtureRequest, type FixtureRequest } from './fixture/fixtureMode.js'
import {
  buildSyntheticRenditions,
  buildSyntheticRoot,
  createSyntheticLedgerSource,
  type SyntheticLedgerSource,
} from './fixture/syntheticLedgerSource.js'
import { createRealLedgerSource } from './fixture/realLedgerSource.js'
import type { SegmentVerificationResult } from './SegmentVerifier.js'
import { useVerificationLedger } from './ledger/useVerificationLedger.js'
import { useSimulatedVerification } from './useSimulatedVerification.js'

/** The five props this component owns; a caller supplies everything else. */
type OwnedPlayerProps =
  | 'subtitleLanguages'
  | 'verifier'
  | 'verificationFailure'
  | 'verificationControl'
  | 'verificationOverlay'

/**
 * What the page knows about this video's place in a container, and the chain
 * does not. Optional in full: a page that has not resolved a container still
 * gets a working player, with the versions axis reading `unknown` — which is
 * what not having looked means.
 */
export interface VersionContext {
  /** Which container's on-chain version list to read. Equal to version 1's
   * video UUID, so it is a pointer like every other the API supplies. */
  containerId?: string
  /** The wallet the CONTAINER was anchored under, from `container.chain`.
   * Needed because a version that was never anchored carries no authority of
   * its own, and that is the case where the chain's list most needs reading.
   * See useVideoVerification for what happens when the two disagree. */
  containerAuthority?: string
  /** Versions whose bytes the publisher deleted. The chain has no concept of
   * deletion, so this can only ever come from the container JSON. */
  deletedVideoIds?: string[]
  /** Where a viewer who wants the current version should go. */
  currentHref?: string
  /** The container's version list exactly as the API served it. Read ONLY by
   * the simulated demo path (useSimulatedVerification), which has no chain to
   * read one from; the real path ignores it and takes the chain's list, which
   * is the whole point of anchoring one. */
  versionList?: ContainerVersion[]
}

export interface VerifiedPlayerProps {
  /** The video as served by `GET /api/v1/public/videos/:id`. Its
   * `publication.chain` is only ever used as a POINTER — where to look on
   * chain — never as a truth claim (Decision 1). */
  video: Video
  /** Everything the page would have handed VideoPlayer before verification
   * existed. The verification props are supplied here instead, so a page
   * cannot pass a half-wired set by accident. */
  player: Omit<VideoPlayerProps, OwnedPlayerProps>
  // NO `compact`. It existed only to tell the deleted badge and panel to drop
  // their caption line in an iframe, and T14 removed all three. The surfaces
  // that replaced them need no such flag: the glyph is one 40x40 control that
  // is deliberately kept at every width (it is not decorative), and the panel
  // is sized in percentages of the player, so an embed gets a smaller panel
  // without being told it is an embed.
  /** See VersionContext. */
  versions?: VersionContext
}

/**
 * The verifying path. Mounted whenever `verificationPointer` found an
 * authority to derive a chain read from — the video's own, or (since S1)
 * its container's when the video carries none of its own — and only
 * underneath a ConnectionProvider; `useVideoVerification` reads the chain
 * directly and needs both.
 */
const VerifiedPlayerInner: FC<VerifiedPlayerProps> = ({ video, player, versions }) => {
  // THE ONE PIECE OF INDIRECTION IN THIS FILE, and it is here because the two
  // hooks below genuinely need each other: the ledger cannot build its lanes
  // until the sweep has produced a root, and the sweep's segment results have
  // nowhere to go until the ledger exists. The sink is therefore a stable
  // callback over a ref, filled in one line after the ledger is created —
  // during render, so it is in place long before any segment can arrive off
  // the network.
  const reportToLedger = useRef<((result: SegmentVerificationResult) => void) | null>(null)
  const onSegmentResult = useCallback((result: SegmentVerificationResult) => {
    reportToLedger.current?.(result)
  }, [])

  const {
    verify,
    anchor,
    chain,
    failure,
    rejectedAddenda,
    versionConsistency,
    container,
    verifier,
    root,
    renditions,
  } = useVideoVerification(video, versions?.containerId, versions?.containerAuthority, onSegmentResult)

  // The real ledger, fed by the real SegmentVerifier hashing real bytes
  // against the hashes the chain sealed. Identical in every respect to the
  // fixture path's ledger below except for where its results come from —
  // which is the property the whole plan rests on.
  const { set, summary, report } = useVerificationLedger(root, renditions)
  reportToLedger.current = report

  // A HALT OPENS THE PANEL AND KEEPS IT OPEN. `modalOpen` is therefore what the
  // viewer asked for OR'd with what happened: once bytes have disagreed the
  // panel is the only account of why the video stopped — the standalone failure
  // card that used to say so was deleted in T12 — so it is not the viewer's to
  // dismiss. The modal itself drops its close button and ignores Escape while a
  // mismatch is set; this is the other half of that, and it is what makes a
  // reopen impossible rather than merely awkward.
  const [modalRequested, setModalRequested] = useState(false)
  const modalOpen = modalRequested || Boolean(failure)

  // The halt in the shape the panel reads. Two sources, in this order:
  //
  //  1. THE LEDGER'S OWN RECORD, when it has one. It is the same event, kept
  //     with both fingerprints — the one the chain sealed and the one that
  //     arrived — and stacking those two is the whole point of the failure
  //     view. `VerificationHalt` carries only the sentence.
  //  2. `mismatchFromHalt(failure)` otherwise: a halt the ledger never saw,
  //     because it happened during the sweep, before any lane existed — a
  //     rendition manifest or a base subtitle disagreeing. failureSurface.ts
  //     carries one shape to the other and is tested, rather than the sentence
  //     being re-typed inside this closure.
  const ledgerMismatch = set?.mismatch ?? null
  const panelMismatch = useMemo(
    () => ledgerMismatch ?? (failure ? mismatchFromHalt(failure) : null),
    [ledgerMismatch, failure],
  )

  // The overlay of Decision 2, at the point it becomes visible: the sealed
  // base's languages exactly as the page would have passed them, plus any
  // addendum track, minus any addendum whose bytes failed its own on-chain
  // hash. With no addenda this returns the base list unchanged — the superset
  // invariant, which is why an anchored video with none renders the same
  // picker it always did.
  const baseLanguages = video.subtitles?.languages
  const addenda = video.publication?.addenda
  const subtitleTracks = useMemo(
    () => mergeSubtitleTracks(baseLanguages, addenda, rejectedAddenda),
    [baseLanguages, addenda, rejectedAddenda],
  )

  // Where this video's own account lives. Derived rather than taken from the
  // API's recorded `video_pda`, for the same reason every other derivation
  // here is: the API supplies an authority to look under, and the address is
  // ours to compute from it. Degrades to undefined on a malformed authority,
  // which drops one link from the panel and breaks nothing.
  const authority = video.publication?.chain?.authority
  const videoAddress = useMemo(() => {
    if (!authority) return undefined
    try {
      return videoPda(publisherPda(new PublicKey(authority)), videoIdBytes(video.id)).toBase58()
    } catch {
      return undefined
    }
  }, [authority, video.id])

  // The chain half of the panel: the anchor state in words, the correction
  // window, the explorer links and the version list. The same shape the
  // fixture path builds — see SimulatedPlayerInner — except that every field
  // here was read off the chain.
  const provenance = useMemo(
    () => ({
      verify,
      anchor,
      chain,
      videoAddress: videoAddress ?? null,
      container,
      consistency: versionConsistency,
      playingVideoId: video.id,
      deletedVideoIds: versions?.deletedVideoIds,
      checkedCount: summary?.checkedCount ?? 0,
    }),
    [verify, anchor, chain, videoAddress, container, versionConsistency, video.id, versions?.deletedVideoIds, summary],
  )

  return (
    <VideoPlayer
      {...player}
      subtitleLanguages={subtitleTracks}
      verifier={verifier}
      verificationFailure={failure}
      verificationControl={(ctx) => (
        <VerificationGlyph
          open={modalOpen}
          failed={Boolean(failure)}
          openColor={ctx.openIcon}
          onClick={() => setModalRequested((open) => !open)}
        />
      )}
      verificationOverlay={(ctx) =>
        modalOpen ? (
          <VerificationModal
            summary={summary}
            mismatch={panelMismatch}
            provenance={provenance}
            getTime={ctx.getTime}
            getBufferedEnd={ctx.getBufferedEnd}
            onClose={() => setModalRequested(false)}
          />
        ) : null
      }
    />
  )
}

/**
 * Drives T8's fixture ledger. Renders nothing.
 *
 * IT IS MOUNTED FOR THE WHOLE LIFE OF THE PLAYER, not just while the modal is
 * open, and that is the point. It used to live inside the overlay slot, which
 * meant closing the panel unmounted the ledger and threw away every slice it
 * had recorded; reopening it started from an empty chart, as if nothing had
 * ever been checked. That was wrong twice over — it lost real evidence, and it
 * misrepresented what verification does. The real thing hashes bytes as they
 * arrive off the network whether or not anyone has the panel open, so the
 * fixture does too, and the panel is only ever a window onto a ledger that was
 * already being kept.
 *
 * Lives in its own component, not inline in a slot closure, because `ctx` only
 * exists inside VideoPlayer's render: a closure may return JSX built from it,
 * but calling a hook directly inside the closure would run that hook against
 * VideoPlayer's own instance rather than this one's.
 *
 * A 250ms interval rather than an animation frame: the source only has
 * something new to say once per slice, which is every few seconds.
 */
const FixtureLedgerDriver: FC<{
  ctx: PlayerVerificationContext
  source: SyntheticLedgerSource | null
  report: (result: SegmentVerificationResult) => void
  onDuration: (duration: number) => void
}> = ({ ctx, source, report, onDuration }) => {
  const { duration, getTime } = ctx

  useEffect(() => {
    onDuration(duration)
  }, [duration, onDuration])

  useEffect(() => {
    if (!source) return undefined
    const step = () => {
      for (const result of source.advance(getTime())) report(result)
    }
    step()
    const timer = window.setInterval(step, 250)
    return () => window.clearInterval(timer)
  }, [source, report, getTime])

  return null
}

/**
 * The SIMULATED path — demo only, reachable only when the URL carries
 * `?verify=fixture`. See fixture/fixtureMode.ts and useSimulatedVerification.ts.
 *
 * Written as its own component with its own JSX rather than as a flag inside
 * `VerifiedPlayerInner`, for the same reason the unanchored branch is its own
 * path: the real verifying path must stay provably untouched by this, and a
 * reader must be able to confirm that by seeing that no demo value can reach
 * it. Nothing here mounts a ConnectionProvider — there is no chain read to
 * make — so a demo build opens no RPC connection at all.
 */
const SimulatedPlayerInner: FC<VerifiedPlayerProps & { fixture: FixtureRequest }> = ({
  video,
  player,
  versions,
  fixture,
}) => {
  const { source: fixtureSource, failSlice } = fixture
  const { verify, anchor, chain, versionConsistency, container, videoPda: simulatedPda } = useSimulatedVerification(
    video,
    versions?.containerId,
    versions?.versionList,
  )

  // The ledger lives HERE, above the modal, so closing the panel does not
  // destroy what has been checked. See FixtureLedgerDriver.
  const [duration, setDuration] = useState(0)
  const root = useMemo(
    () => (duration > 0 ? buildSyntheticRoot(video.id, duration) : null),
    [video.id, duration],
  )
  const renditions = useMemo(() => buildSyntheticRenditions(video.id, duration), [video.id, duration])
  const { set, summary, report } = useVerificationLedger(root, renditions)

  // TWO SOURCES, ONE LEDGER, AND ONLY EVER ONE OF THEM ALIVE. Layer 1 is
  // clock-driven and invents the bytes as well as the answer; layer 2
  // (`?verify=fixture&source=real`) hashes the media segments VHS actually
  // fetched and fabricates the expectation to match. Everything above this
  // line — the lanes, the composite, the chart, the halt — cannot tell which
  // one is reporting, which is the property the whole plan rests on.
  //
  // Both are built from the SAME `duration`, because both write into the lane
  // `buildSyntheticRoot(video.id, duration)` fabricated: a result naming a
  // cell that lane does not have is silently dropped by `applyResult`.
  const syntheticSource = useMemo(
    () =>
      duration > 0 && fixtureSource === 'synthetic'
        ? createSyntheticLedgerSource({ videoId: video.id, duration, failSlice })
        : null,
    [video.id, duration, failSlice, fixtureSource],
  )

  // Layer 2 is PUSHED, not pulled: there is no clock to advance because the
  // network is the clock, so it reports straight into the ledger as bytes
  // land. `report` is a stable useCallback (useVerificationLedger), so this
  // memo does not churn and the source is not rebuilt under the player.
  const realSource = useMemo(
    () =>
      duration > 0 && fixtureSource === 'real'
        ? createRealLedgerSource({ videoId: video.id, duration, failSlice, onResult: report })
        : null,
    [video.id, duration, failSlice, fixtureSource, report],
  )

  // THE FIXTURE HALTS THE PLAYER FOR REAL. A scripted mismatch is only worth
  // showing if it does what a genuine one does: the ledger's sticky mismatch is
  // carried into the shape VideoPlayer enforces (failureSurface.ts), so the
  // player pauses, refuses to play again for the session and draws the scrim
  // through exactly the same code path the real halt takes. Nothing here is a
  // demo-only branch inside the player.
  //
  // Memoized on the mismatch itself so the halt object is stable: VideoPlayer's
  // pause effect is keyed on it.
  const mismatch = set?.mismatch ?? null
  const halt = useMemo(() => (mismatch ? haltFromMismatch(mismatch) : null), [mismatch])

  // As on the real path: a halt opens the panel and the viewer cannot close it.
  const [modalRequested, setModalRequested] = useState(false)
  const modalOpen = modalRequested || Boolean(halt)

  // No addendum is ever rejected here (nothing is checked), so this is the
  // base languages plus every addendum the publisher recorded — the superset
  // the real path produces when everything passes.
  const subtitleTracks = useMemo(
    () => mergeSubtitleTracks(video.subtitles?.languages, video.publication?.addenda, []),
    [video.subtitles?.languages, video.publication?.addenda],
  )

  // The chain half of the panel. Everything here is scripted — see
  // useSimulatedVerification.ts — but it is the same shape the real path
  // produces, so the panel below is the panel that ships.
  const provenance = useMemo(
    () => ({
      verify,
      anchor,
      chain,
      videoAddress: simulatedPda,
      container,
      consistency: versionConsistency,
      playingVideoId: video.id,
      checkedCount: summary?.checkedCount ?? 0,
    }),
    [verify, anchor, chain, simulatedPda, container, versionConsistency, video.id, summary],
  )

  return (
    <VideoPlayer
      {...player}
      subtitleLanguages={subtitleTracks}
      // LAYER 2 RIDES THE PLAYER'S EXISTING VERIFIER SLOT, and nothing was
      // added to VideoPlayer to let it. The player's only interaction with
      // whatever it is handed here is `attachSegmentVerifier`, which calls
      // `attach(player)` and returns `() => detach()` — the exact contract
      // `RealLedgerSource` mirrors on purpose (see realLedgerSource.ts). That
      // effect is keyed on the object, so the source is detached when this
      // component unmounts AND whenever a new one replaces it.
      //
      // The prop is typed `SegmentSource` — the two methods a player actually
      // uses — rather than the `SegmentVerifier` class, so this needs no cast.
      // It was a cast when first written, which meant "the player only ever
      // calls attach and detach" lived in a comment the compiler could not
      // check; naming the contract instead makes it a compile error to break
      // it. `undefined` on the synthetic path means the player is not read,
      // not listened to and not hooked at all.
      verifier={realSource ?? undefined}
      verificationFailure={halt}
      verificationControl={(ctx) => (
        <>
          {/* Renders nothing. Kept in the control slot because that slot is
              mounted for as long as the player is, which is exactly how long
              the ledger has to outlive the panel. On the real-bytes path
              `source` is null and this drives nothing but `duration` — which
              both sources are sized from, so it is still required. */}
          <FixtureLedgerDriver ctx={ctx} source={syntheticSource} report={report} onDuration={setDuration} />
          <VerificationGlyph
            open={modalOpen}
            failed={Boolean(mismatch)}
            openColor={ctx.openIcon}
            onClick={() => setModalRequested((open) => !open)}
          />
        </>
      )}
      verificationOverlay={(ctx) =>
        modalOpen ? (
          <VerificationModal
            summary={summary}
            mismatch={mismatch}
            provenance={provenance}
            getTime={ctx.getTime}
            getBufferedEnd={ctx.getBufferedEnd}
            onClose={() => setModalRequested(false)}
          />
        ) : null
      }
    />
  )
}

/**
 * A video player that checks what it is playing against the chain, for a
 * viewer with no account and no wallet.
 *
 * Splits on one question — does the API point at a chain record at all? — and
 * the two branches are genuinely different code paths rather than one path
 * with a flag, because the unanchored branch has to be provably the same
 * player the page rendered before this existed.
 */
const VerifiedPlayer: FC<VerifiedPlayerProps> = (props) => {
  // Fixture mode only, and decided before anything else so that no chain
  // hook, provider or pointer logic below runs at all in that mode. This is a
  // runtime check of the URL, not a build-time flag — see fixtureMode.ts for
  // why. An ordinary page load carries no `?verify=fixture` and always takes
  // the real branch.
  const fixture = activeFixtureRequest()
  if (fixture) return <SimulatedPlayerInner {...props} fixture={fixture} />
  return <RealVerifiedPlayer {...props} />
}

const RealVerifiedPlayer: FC<VerifiedPlayerProps> = ({ video, player, versions }) => {
  // The pointer, not the proof. An authority is what a PDA is derived from;
  // whether an account actually lives at that address is the chain's answer,
  // read in useChainRecord. Neither the video's own authority nor its
  // container's means there is nothing to look up, so there is nothing to
  // check and nothing to mount. A version with no authority of its own but
  // whose container WAS anchored still needs the verifying path: the
  // container's version list is the only thing that can tell a viewer this
  // version is not the one the chain names (verificationPointer.ts).
  const pointer = verificationPointer(video.publication?.chain?.authority, versions?.containerAuthority)

  const endpoint = useMemo(() => rpcEndpoint(solanaCluster, clusterRpcUrl()), [])
  const config = useMemo(() => ({ wsEndpoint: wsEndpoint(solanaCluster, clusterWsUrl()) }), [])

  if (!pointer) {
    // NO V AT ALL, and no panel behind it. There is nothing on the chain for
    // this video, so there is nothing to check it against and no claim to
    // make. A glyph here would be a mark on a video the product is saying
    // nothing about, which spends the mark's meaning for nothing; the V has
    // to mean "this is being checked" everywhere it appears or it means
    // nothing anywhere. Architecture §7 and T15 acceptance #6.
    return <VideoPlayer {...player} subtitleLanguages={video.subtitles?.languages} />
  }

  return (
    <ConnectionProvider endpoint={endpoint} config={config}>
      <VerifiedPlayerInner video={video} player={player} versions={versions} />
    </ConnectionProvider>
  )
}

export default VerifiedPlayer
